import { Container, ThemeProvider, ToastProvider } from "@myob/myob-widgets";
import { Outlet, useNavigate, useNavigation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useState } from "react";
import { getCurrentUser, checkPurchasedLeaveWindow } from "../../apis/kilnBackendApis";
import Header from "./Header";
import CardWithLoadingStatus from "./CardWithLoadingStatus";
import UserContext from "../../auth/UserContext";
import PurchasedLeaveWindowContext from "../../auth/PurchasedLeaveWindowContext";
import { redirectRequest } from "../../auth/msalConfig";
import { CustomNavigationClient } from "../../auth/CustomNavigationClient";
import { mainContainerStyle } from "../../styles/custom.css.ts";

const PageLayout = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userLoggedIn, setUserLoggedIn] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(true);
  // null = not checked yet, otherwise { windowOpen, pxWindowOpen } once the window-check API has answered
  // (or failed, which we treat as both closed)
  const [purchasedLeaveWindow, setPurchasedLeaveWindow] = useState(null);
  const navigation = useNavigation();
  // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  instance.setNavigationClient(navigationClient);

  useEffect(() => {
    const getAndSaveCurrentUser = async () => {
      setUserLoggedIn((await getCurrentUser()).data);
    };
    const getAndSavePurchasedLeaveWindowOpen = async () => {
      try {
        const response = await checkPurchasedLeaveWindow();
        setPurchasedLeaveWindow(
          response.status === 200
            ? { windowOpen: response.data?.windowOpen === true, pxWindowOpen: response.data?.pxWindowOpen === true }
            : { windowOpen: false, pxWindowOpen: false },
        );
      } catch (err) {
        // Any failure (network, auth, backend not deployed yet) is treated as closed, fail-safe.
        setPurchasedLeaveWindow({ windowOpen: false, pxWindowOpen: false });
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance
        .acquireTokenRedirect({
          ...redirectRequest,
          account: instance.getActiveAccount(),
        })
        // eslint-disable-next-line no-console
        .catch((err) => console.error(err));
    }
    if (!userLoggedIn && isAuthenticated) {
      // eslint-disable-next-line no-console
      getAndSaveCurrentUser().catch((err) => console.error(err));
    }
    if (purchasedLeaveWindow === null && isAuthenticated) {
      getAndSavePurchasedLeaveWindowOpen();
    }
  }, [inProgress, instance, isAuthenticated, userLoggedIn, purchasedLeaveWindow]);

  return (
    <UserContext.Provider value={userLoggedIn}>
      <PurchasedLeaveWindowContext.Provider
        value={{
          windowOpen: purchasedLeaveWindow?.windowOpen === true,
          pxWindowOpen: purchasedLeaveWindow?.pxWindowOpen === true,
        }}
      >
        <ThemeProvider theme={currentTheme ? "modern" : "classic"}>
          <Header theme={currentTheme} setTheme={setCurrentTheme} />
          <ToastProvider>
            <Container className={mainContainerStyle}>
              {navigation.state !== "idle" ? <CardWithLoadingStatus /> : <Outlet />}
            </Container>
          </ToastProvider>
        </ThemeProvider>
      </PurchasedLeaveWindowContext.Provider>
    </UserContext.Provider>
  );
};

export default PageLayout;
